import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FERTILITY_DEMO_SECTION_BORDER } from '../designSystem';
import { NovaUnifiedPageBar } from '../components/NovaUnified';
import './FertilityDemoLoss.css';

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const GROUPS = [
  { id: 'early', label: 'Early pregnancy loss' },
  { id: 'medical', label: 'Medical complication' },
  { id: 'later', label: 'Later loss' },
  { id: 'recurrent', label: 'Multiple or recurrent losses' },
  { id: 'tfmr', label: 'Loss after a diagnosis' },
  { id: 'unsure', label: "I'm not sure" },
];

const CONTENT = {
  chemical: {
    icon: '🌱', title: 'Chemical pregnancy',
    sub: "Early loss support — your grief is real and valid, even if others don't always understand it",
    callout: "Chemical pregnancies are the most common type of loss and among the least talked about. Many women grieve alone because the pregnancy was so early. What you're feeling is completely real.",
    clinical: ['What a chemical pregnancy is and what to expect physically', "Managing the emotional gap between a positive test and a loss", "When to contact your clinic and what they'll check", "When it's safe to try again", 'Questions to ask your RE about your next cycle'],
    women: [{ cls: 'av-pk', init: 'SK', name: 'Sarah K.', sub: 'Chemical · 4 months ago · Columbus' }, { cls: 'av-sg', init: 'JR', name: 'Jess R.', sub: 'Chemical · 2x experience · Cleveland' }, { cls: 'av-wm', init: 'NL', name: 'Nina L.', sub: 'Chemical · trying again now' }],
    forums: [{ icon: '🌱', name: 'Chemical & early loss', meta: '847 members · quiet room' }, { icon: '💬', name: 'When no one else understands', meta: '1,240 members' }, { icon: '🔁', name: 'Trying again after early loss', meta: '2,100 members' }],
  },
  blighted: {
    icon: '🌕', title: 'Blighted ovum',
    sub: 'Finding out at a scan with no symptoms — understanding what happened and what comes next',
    callout: "A blighted ovum is one of the most confusing losses — everything looked like a pregnancy from the outside. The sac was real. Your hope was real. Finding out at a scan with no warning is a particular kind of shock.",
    clinical: ['What a blighted ovum is and why it happens', 'Your options: waiting naturally, misoprostol, or D&C', 'What the D&C involves and what to bring', 'Recovery physically and emotionally', 'Understanding chromosomal causes and future risk'],
    women: [{ cls: 'av-pp', init: 'MT', name: 'Mia T.', sub: 'Blighted ovum · 5 months ago' }, { cls: 'av-pk', init: 'CN', name: 'Cara N.', sub: 'Blighted ovum · D&C · Cleveland' }, { cls: 'av-sg', init: 'LS', name: 'Leah S.', sub: 'Blighted ovum · 2nd pregnancy' }],
    forums: [{ icon: '🌕', name: 'Blighted ovum support', meta: '520 members' }, { icon: '🏥', name: 'After a D&C', meta: '1,240 members' }, { icon: '🔁', name: 'Trying again after early loss', meta: '2,100 members' }],
  },
  missed: {
    icon: '🫧', title: 'Missed miscarriage',
    sub: 'Found at a scan with no warning — the shock of no symptoms and a sudden goodbye',
    callout: "A missed miscarriage means your body hadn't let go yet. You may have walked in to what felt like a routine scan. There's a particular kind of grief in finding out this way — and a set of choices you now have to make that feel impossible.",
    clinical: ['Understanding why missed miscarriages happen', 'Your three options: expectant management, medication, or D&C', 'What each option involves — honestly, not clinically', 'The emotional experience of each path', 'What to watch for during recovery'],
    women: [{ cls: 'av-wm', init: 'KB', name: 'Katy B.', sub: 'Missed MMC · 8 weeks found at 12 · Columbus' }, { cls: 'av-pk', init: 'RM', name: 'Rachel M.', sub: 'Missed MMC · chose D&C · Cincinnati' }, { cls: 'av-pp', init: 'AL', name: 'Anna L.', sub: 'Missed MMC · waited naturally' }],
    forums: [{ icon: '🫧', name: 'Missed miscarriage', meta: '780 members · quiet room' }, { icon: '🏥', name: 'After a D&C', meta: '1,240 members' }, { icon: '💬', name: 'The shock of finding out at a scan', meta: '430 members' }],
  },
  first: {
    icon: '🌸', title: 'First trimester loss',
    sub: 'Before 13 weeks — support, D&C information, and community who understand',
    callout: null,
    clinical: ['What to expect at a D&C — what to bring, what happens, what after', 'Recovery at home day by day', "The hormonal crash as hCG drops — what's normal", 'When to expect your next period', "Talking to people around you who may not know what to say"],
    women: [{ cls: 'av-pk', init: 'JM', name: 'Jasmine M.', sub: 'First trimester · D&C · 6 months ago' }, { cls: 'av-sg', init: 'NR', name: 'Noelle R.', sub: 'First trimester · 3 months ago · Cleveland' }, { cls: 'av-pp', init: 'SL', name: 'Sofia L.', sub: 'First trimester · now pregnant again' }],
    forums: [{ icon: '🌸', name: 'First trimester loss', meta: '1,840 members' }, { icon: '🏥', name: 'After a D&C', meta: '1,240 members' }, { icon: '💕', name: 'Pregnancy after loss', meta: '3,200 members' }],
  },
  ectopic: {
    icon: '⚕️', title: 'Ectopic pregnancy',
    sub: 'A medical emergency that was also a loss — both things are true',
    callout: "An ectopic pregnancy requires fast medical action, which means grief often comes later — after the emergency is over and the adrenaline fades. The loss and the trauma of treatment are separate griefs that sometimes arrive at different times.",
    clinical: ['Understanding what happened and why', 'Methotrexate vs surgery — what each involves', 'Whether your tube was affected and what that means for future fertility', 'Physical recovery and when hCG returns to zero', "When it's safe to try again — typically 3 months post-methotrexate"],
    women: [{ cls: 'av-wm', init: 'JM', name: 'Jamie M.', sub: 'Ectopic · methotrexate · Cincinnati' }, { cls: 'av-pk', init: 'CT', name: 'Claire T.', sub: 'Ectopic · surgery · one tube · Columbus' }, { cls: 'av-sg', init: 'PK', name: 'Priya K.', sub: 'Ectopic · pregnant again 8 months later' }],
    forums: [{ icon: '⚕️', name: 'Ectopic & tube health', meta: '392 members · moderated' }, { icon: '💬', name: 'Grief after a medical emergency', meta: '218 members' }, { icon: '🔁', name: 'TTC after ectopic', meta: '640 members' }],
  },
  molar: {
    icon: '🔬', title: 'Molar pregnancy',
    sub: "An unusual loss with a long recovery — understanding what happened and what's ahead",
    callout: "A molar pregnancy is one of the least understood losses. It's not a \"normal\" miscarriage — it requires ongoing monitoring, and you're told you cannot try again for 6–12 months. That waiting period, while medically necessary, is its own grief.",
    clinical: ['What a complete vs partial molar pregnancy means', 'Why ongoing hCG monitoring is essential', 'Understanding GTN risk and when to be checked', "The 6–12 month waiting period and what it involves", 'Questions for your oncology-gynecology team'],
    women: [{ cls: 'av-pp', init: 'DR', name: 'Dana R.', sub: 'Complete molar · monitoring month 4' }, { cls: 'av-wm', init: 'BC', name: 'Beth C.', sub: 'Partial molar · cleared to try again' }, { cls: 'av-pk', init: 'SM', name: 'Sara M.', sub: 'Molar · GTN treated · now pregnant' }],
    forums: [{ icon: '🔬', name: 'Molar pregnancy support', meta: '214 members · specialised' }, { icon: '⏳', name: 'The waiting period', meta: '310 members' }, { icon: '💬', name: 'Explaining molar loss to others', meta: '180 members' }],
  },
  second: {
    icon: '🤍', title: 'Second trimester loss',
    sub: 'Between 13–24 weeks — a loss that often requires more from your body and carries more weight',
    callout: "Second trimester losses are often more complex medically — involving induction, D&E, or other procedures — and more visible in your life. You may have told people. You may have started to plan. The grief is different in scale.",
    clinical: ['Understanding the D&E procedure and what to expect', 'Labour induction at this stage — what it involves', "Physical recovery and what's normal", 'Certificates and documentation some states require', 'When to return for follow-up and what to ask'],
    women: [{ cls: 'av-wm', init: 'MT', name: 'Michelle T.', sub: '18 weeks · D&E · 8 months ago' }, { cls: 'av-pk', init: 'LK', name: 'Laura K.', sub: '16 weeks · Columbus · trying again' }, { cls: 'av-pp', init: 'DB', name: 'Diane B.', sub: '20 weeks · support group' }],
    forums: [{ icon: '🤍', name: 'Second trimester loss', meta: '410 members · moderated' }, { icon: '💬', name: 'When people already knew', meta: '280 members' }, { icon: '🕊', name: 'Later loss community', meta: '320 members' }],
  },
  stillbirth: {
    icon: '🕊', title: 'Late-term loss & stillbirth',
    sub: 'You held a child. This support is for you.',
    callout: 'Stillbirth is a profoundly different kind of grief. There may be a birth, a goodbye, decisions about memory-making, and a physical recovery from delivery. There is specialised support here, and connection to organisations that understand what no one else in your life may.',
    clinical: ['What happens during and after a stillbirth delivery', 'Memory-making — holding, photographs, footprints', 'Certificates of birth and bereavement leave', 'Physical recovery after a full-term delivery', 'Organisations: SHARE, Star Legacy Foundation, NILMDTS'],
    women: [{ cls: 'av-wm', init: 'AT', name: 'Anna T.', sub: 'Stillbirth 34 weeks · 1 year ago' }, { cls: 'av-pp', init: 'KR', name: 'Kim R.', sub: '28 weeks · Star Legacy mentor' }, { cls: 'av-pk', init: 'GL', name: 'Grace L.', sub: 'Stillbirth · pregnant again · Ohio' }],
    forums: [{ icon: '🕊', name: 'Stillbirth & late loss', meta: '218 members · carefully moderated' }, { icon: '💜', name: 'Memory-making & goodbye', meta: '140 members' }, { icon: '💕', name: 'Pregnancy after stillbirth', meta: '890 members' }],
  },
  rpl: {
    icon: '🔁', title: 'Recurrent pregnancy loss',
    sub: 'Two or more losses — a different medical and emotional reality',
    callout: 'RPL is not just "more loss." It\'s a separate medical path — with workups, testing, immune protocols, and decisions most people around you won\'t understand. The emotional toll of trying and losing repeatedly is its own kind of trauma.',
    clinical: ['What an RPL workup involves — tests, timelines, what they\'re looking for', 'Chromosomal, immune, and anatomical causes', 'Progesterone supplementation and when it helps', 'Questions to ask your RE about RPL protocols', 'Understanding "unexplained" RPL and what to do next'],
    women: [{ cls: 'av-pp', init: 'MK', name: 'Melissa K.', sub: '3 losses · RPL workup complete · Columbus' }, { cls: 'av-wm', init: 'TN', name: 'Tara N.', sub: '4 losses · immune protocol · pregnant' }, { cls: 'av-pk', init: 'BL', name: 'Brooke L.', sub: '2 losses · unexplained · Cleveland' }],
    forums: [{ icon: '🔁', name: 'Recurrent pregnancy loss', meta: '1,420 members · active' }, { icon: '🔬', name: 'RPL workup & testing', meta: '840 members' }, { icon: '💕', name: 'Pregnancy after RPL', meta: '1,100 members' }],
  },
  tfmr: {
    icon: '💜', title: 'Termination for medical reasons',
    sub: 'A decision made out of love — a loss that deserves grief, community, and care',
    callout: "TFMR is one of the most isolating losses in women's health. You made an impossibly hard decision. You may be grieving in silence because others don't understand — or because you haven't told anyone. This community is completely private from the main fertility feed.",
    clinical: ['Understanding the diagnosis that led to this decision', 'What the procedure involves at different gestations', 'Physical recovery and follow-up care', 'The emotional uniqueness of TFMR grief', 'Organisations that specialise in TFMR support: TFMR for Medical Reasons'],
    women: [{ cls: 'av-wm', init: '•', name: 'Private member', sub: 'TFMR · T21 diagnosis · 20 weeks' }, { cls: 'av-pp', init: '•', name: 'Private member', sub: 'TFMR · cardiac defect · mentor' }, { cls: 'av-pk', init: '•', name: 'Private member', sub: 'TFMR · pregnant again · Ohio' }],
    forums: [{ icon: '💜', name: 'TFMR — private community', meta: '380 members · strictly private' }, { icon: '💬', name: "Grief others don't understand", meta: '420 members' }, { icon: '💕', name: 'After TFMR — what comes next', meta: '290 members' }],
  },
};

const warmStyles = {
  bg: '#f8f4ef',
  bg2: '#ede5d8',
  border: '#c4a882',
  text: '#3d2c1e',
  muted: '#7a6050',
};

const PAGE_BG = 'rgb(249, 246, 241)';
const CARD_BG = 'rgb(255, 252, 249)';
const SECTION_CARD = {
  background: CARD_BG,
  border: FERTILITY_DEMO_SECTION_BORDER,
  borderRadius: 14,
  padding: '14px 16px',
};

/** Six primary loss paths → existing CONTENT keys (preserves step-2 routing). */
const LOSS_TYPE_PILLS = [
  {
    emoji: '🌱',
    name: 'Early pregnancy loss',
    sub: 'Before 12 weeks · chemical · blighted ovum',
    onSelect: (selectType, setActiveGroup) => {
      setActiveGroup('early');
      selectType('first');
    },
  },
  {
    emoji: '🩺',
    name: 'Medical complication',
    sub: 'Ectopic · molar pregnancy · TFMR',
    onSelect: (selectType, setActiveGroup) => {
      setActiveGroup('medical');
      selectType('ectopic');
    },
  },
  {
    emoji: '🕊️',
    name: 'Later loss',
    sub: 'After 12 weeks · stillbirth',
    onSelect: (selectType, setActiveGroup) => {
      setActiveGroup('later');
      selectType('second');
    },
  },
  {
    emoji: '🔁',
    name: 'Multiple or recurrent losses',
    sub: 'RPL · two or more losses',
    onSelect: (selectType, setActiveGroup) => {
      setActiveGroup('recurrent');
      selectType('rpl');
    },
  },
  {
    emoji: '💙',
    name: 'Loss after a diagnosis',
    sub: 'Chromosomal · genetic · fetal anomaly',
    onSelect: (selectType, setActiveGroup) => {
      setActiveGroup('tfmr');
      selectType('tfmr');
    },
  },
  {
    emoji: '💬',
    name: "I'm not sure",
    sub: 'We can help you understand what happened',
    onSelect: (selectType, setActiveGroup) => {
      setActiveGroup('unsure');
      selectType('first');
    },
  },
];

export default function FertilityDemoLoss({ navigate, demoChapter = 'fertility' }) {
  const sectionBorder = demoChapter === 'fertility' ? FERTILITY_DEMO_SECTION_BORDER : '1px solid var(--border)';
  const [searchParams] = useSearchParams();
  const groupParam = searchParams.get('group');
  const [activeGroup, setActiveGroup] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (groupParam && GROUPS.some((g) => g.id === groupParam)) {
      setActiveGroup(groupParam);
      if (groupParam === 'tfmr') {
        setSelectedType('tfmr');
        setStep(2);
      }
    }
  }, [groupParam]);

  const showGroup = (id) => {
    setActiveGroup(id);
    if (id === 'tfmr') {
      setTimeout(() => {
        setSelectedType('tfmr');
        setStep(2);
      }, 100);
    }
  };

  const selectType = (id) => {
    setSelectedType(id);
    setStep(2);
    window.scrollTo(0, 0);
  };

  const goBack = () => {
    setStep(1);
    window.scrollTo(0, 0);
  };

  const content = selectedType && CONTENT[selectedType];

  if (step === 2 && content) {
    return (
      <div style={{ fontFamily: FONT_BODY, padding: '16px 16px 24px', background: PAGE_BG, boxSizing: 'border-box', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: warmStyles.bg2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{content.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{content.title}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{content.sub}</div>
          </div>
          <button type="button" onClick={goBack} style={{ fontFamily: 'inherit', fontSize: 11, fontWeight: 600, color: 'var(--muted)', background: 'var(--sand)', border: '1px solid var(--border)', borderRadius: 999, padding: '5px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>← Change</button>
        </div>

        {content.callout && (
          <div style={{ background: warmStyles.bg, border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 16 }}>🕊</span>
            <div style={{ fontSize: 13, color: warmStyles.text, lineHeight: 1.55 }}>{content.callout}</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div style={{ background: 'var(--white)', border: sectionBorder, borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>🏥</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>What to expect</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, marginBottom: 14 }}>
              {content.clinical.map((item, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--text)', display: 'flex', alignItems: 'baseline', gap: 7, lineHeight: 1.45 }}>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: warmStyles.border, flexShrink: 0, marginTop: 5 }} />
                  {item}
                </div>
              ))}
            </div>
            <button type="button" onClick={() => navigate('/signup')} style={{ fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: warmStyles.text, background: warmStyles.bg2, border: `1px solid ${warmStyles.border}`, borderRadius: 999, padding: '8px 16px', cursor: 'pointer', width: '100%' }}>Read guides →</button>
          </div>
          <div style={{ background: 'var(--white)', border: sectionBorder, borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>💕</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Women who&apos;ve been there</div>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, marginBottom: 12 }}>
              {content.women.map((w, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: i < content.women.length - 1 ? '1px solid #EDE8DF' : 'none' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, background: w.cls === 'av-pk' ? '#F4C0D1' : w.cls === 'av-sg' ? '#9FE1CB' : w.cls === 'av-pp' ? '#afa9ec' : warmStyles.bg2, color: w.cls === 'av-pk' ? '#993556' : w.cls === 'av-sg' ? '#0F6E56' : w.cls === 'av-pp' ? '#534ab7' : warmStyles.text }}>{w.init}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{w.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{w.sub}</div>
                  </div>
                  <button type="button" onClick={() => navigate('/signup')} style={{ fontFamily: 'inherit', fontSize: 10, fontWeight: 600, color: 'var(--muted)', background: 'var(--sand)', border: '1px solid var(--border)', borderRadius: 999, padding: '4px 11px', cursor: 'pointer' }}>Connect</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => navigate('/signup')} style={{ fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: '#993556', background: '#FBEAF0', border: '1px solid #F4C0D1', borderRadius: 999, padding: '8px 16px', cursor: 'pointer', width: '100%' }}>See all matches →</button>
          </div>
          <div style={{ background: 'var(--white)', border: sectionBorder, borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>🤝</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Community & forums</div>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, marginBottom: 12 }}>
              {content.forums.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 0', borderBottom: i < content.forums.length - 1 ? '1px solid #EDE8DF' : 'none' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: warmStyles.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>{f.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{f.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{f.meta}</div>
                  </div>
                  <button type="button" onClick={() => navigate('/signup')} style={{ fontFamily: 'inherit', fontSize: 10, fontWeight: 600, color: 'var(--muted)', background: 'var(--sand)', border: '1px solid var(--border)', borderRadius: 999, padding: '4px 11px', cursor: 'pointer' }}>Join</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => navigate('/signup')} style={{ fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: warmStyles.text, background: warmStyles.bg2, border: `1px solid ${warmStyles.border}`, borderRadius: 999, padding: '8px 16px', cursor: 'pointer', width: '100%' }}>Browse spaces →</button>
          </div>
        </div>

        <div style={{ background: warmStyles.bg2, borderRadius: 14, padding: '16px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: warmStyles.text }}>Ready to talk to Nova about what happened?</div>
            <div style={{ fontSize: 11, color: warmStyles.muted, marginTop: 2 }}>She can sit with you, help you understand your options, or just listen.</div>
          </div>
          <button type="button" onClick={() => navigate('/nova')} style={{ fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: warmStyles.text, background: warmStyles.bg2, border: `1px solid ${warmStyles.border}`, borderRadius: 999, padding: '8px 20px', cursor: 'pointer' }}>Open Nova →</button>
        </div>
      </div>
    );
  }

  const sectionLinkBtn = {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgb(153, 53, 86)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: FONT_BODY,
    padding: 0,
  };

  const lossChapterSteps = [
    {
      emoji: '🌊',
      title: 'The grief comes in waves',
      body: "Some days will be fine. Some won't. This chapter doesn't push you forward — it holds space for wherever you land.",
    },
    {
      emoji: '❓',
      title: "The questions don't stop",
      body: 'Why did this happen. Could it have been prevented. What does it mean for next time. Clinically reviewed answers — without the spiral.',
    },
    {
      emoji: '🔁',
      title: "Deciding what's next is hard",
      body: 'Try again. Take time. Change course. Whatever you choose, your full history is here and this platform moves with you.',
    },
  ];

  return (
    <div
      style={{
        background: PAGE_BG,
        fontFamily: FONT_BODY,
        width: '100%',
        boxSizing: 'border-box',
        padding: '20px 0 28px',
      }}
    >
      <div className="loss-main-col" style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%', minWidth: 0 }}>
        <NovaUnifiedPageBar
          layout="stacked"
          contextPill="Loss support"
          body="You don't have to choose anything right now. This platform is here whenever you're ready — to talk, find information, or just to sit with you."
          actionLabel="Open Nova →"
          onAction={() => navigate('/nova')}
        />

        {/* 2 — Loss selector */}
        <div className="loss-selector-card" style={SECTION_CARD}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <h2 className="section-title">Tell us about your experience</h2>
              <p className="section-descriptor" style={{ color: 'rgb(122, 107, 99)', lineHeight: '18px' }}>
                Every loss is different. Knowing yours helps us surface the right support, community, and information for where you are right now.
              </p>
            </div>
          </div>
          <div className="loss-type-grid" style={{ display: 'grid', gap: 8 }}>
            {LOSS_TYPE_PILLS.map((pill) => (
              <button
                key={pill.name}
                type="button"
                className="loss-type-pill"
                onClick={() => pill.onSelect(selectType, setActiveGroup)}
                style={{
                  background: CARD_BG,
                  border: '1.5px solid rgb(237, 229, 216)',
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  textAlign: 'left',
                  width: '100%',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              >
                <span
                  className="loss-emoji-box"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'rgb(251, 234, 240)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 15,
                    flexShrink: 0,
                  }}
                  aria-hidden
                >
                  {pill.emoji}
                </span>
                <span style={{ minWidth: 0, textAlign: 'left' }}>
                  <span className="loss-pill-name" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'rgb(122, 96, 80)', lineHeight: 1.3 }}>
                    {pill.name}
                  </span>
                  <span className="loss-pill-sub" style={{ display: 'block', fontSize: 10, fontWeight: 400, color: 'rgb(122, 107, 99)', marginTop: 1, lineHeight: 1.3 }}>
                    {pill.sub}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* 3 — Two columns */}
        <div className="loss-two-col" style={{ display: 'grid', gap: 14 }}>
          <div className="loss-chapter-card" style={SECTION_CARD}>
            <div style={{ marginBottom: 14 }}>
              <h2 className="section-title">What this chapter holds</h2>
              <p className="section-descriptor" style={{ color: 'rgb(122, 107, 99)', lineHeight: '18px' }}>
                There&apos;s no right way to move through this. OrivonAI stays with you across all of it.
              </p>
            </div>
            {lossChapterSteps.map((row, i) => (
              <div
                key={row.title}
                className="loss-step-row"
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '9px 0',
                  borderBottom: i < lossChapterSteps.length - 1 ? '0.5px solid rgba(212, 83, 126, 0.12)' : 'none',
                }}
              >
                <span style={{ width: 22, textAlign: 'center', fontSize: 16, flexShrink: 0, marginTop: 1 }} aria-hidden>
                  {row.emoji}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'rgb(17, 17, 17)', marginBottom: 2 }}>{row.title}</div>
                  <div style={{ fontSize: 11, fontWeight: 400, color: 'rgb(122, 107, 99)', lineHeight: 1.5 }}>{row.body}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="loss-community-card" style={SECTION_CARD}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 2, gap: 10 }}>
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <h2 className="section-title">Women who understand</h2>
                <p className="section-descriptor" style={{ color: 'rgb(122, 107, 99)', lineHeight: '18px' }}>
                  Going through the same thing — right now.
                </p>
              </div>
              <button type="button" onClick={() => navigate('/connect')} style={{ ...sectionLinkBtn, flexShrink: 0 }}>
                See all →
              </button>
            </div>

            {[
              { initials: 'SL', name: 'Sara L.', sub: 'RPL · 3rd loss · Columbus, OH' },
              { initials: 'MK', name: 'Maya K.', sub: 'Early loss · first time · trying again' },
              { initials: 'JR', name: 'Jamie R.', sub: 'Loss after IVF · cycle 2 · healing' },
            ].map((p, i, arr) => (
              <div
                key={p.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 0',
                  borderBottom: i < arr.length - 1 ? '0.5px solid rgb(237, 232, 223)' : 'none',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'rgb(251, 234, 240)',
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'rgb(153, 53, 86)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {p.initials}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'rgb(17, 17, 17)' }}>{p.name}</div>
                  <div style={{ fontSize: 11, fontWeight: 400, color: 'rgb(122, 107, 99)' }}>{p.sub}</div>
                </div>
                <button type="button" onClick={() => navigate('/signup')} style={{ ...sectionLinkBtn, marginLeft: 'auto', fontSize: 11, color: 'rgb(212, 83, 126)' }}>
                  Connect →
                </button>
              </div>
            ))}

            <div style={{ borderTop: '0.5px solid rgba(212, 83, 126, 0.12)', margin: '10px 0 8px' }} />

            {[
              {
                av: 'AL',
                dot: true,
                name: 'After Loss — Trying Again',
                sub: '2,870 members · RPL',
              },
              {
                av: 'GR',
                dot: false,
                name: 'Grief & Recovery',
                sub: '980 members · private',
              },
            ].map((g, i, arr) => (
              <div
                key={g.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 0',
                  borderBottom: i < arr.length - 1 ? '0.5px solid rgba(212, 83, 126, 0.12)' : 'none',
                }}
              >
                <div style={{ position: 'relative', width: 28, height: 28, flexShrink: 0 }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: 'rgba(153, 53, 86, 0.12)',
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'rgb(153, 53, 86)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {g.av}
                  </div>
                  {g.dot ? (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: -1,
                        right: -1,
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: 'rgb(34, 197, 94)',
                        border: '1.5px solid rgb(255, 252, 249)',
                      }}
                      aria-hidden
                    />
                  ) : null}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'rgb(45, 26, 34)' }}>{g.name}</div>
                  <div style={{ fontSize: 11, fontWeight: 400, color: 'rgb(122, 107, 99)' }}>{g.sub}</div>
                </div>
                <button type="button" onClick={() => navigate('/signup')} style={{ ...sectionLinkBtn, marginLeft: 'auto', fontSize: 11 }}>
                  Join →
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
